/**
 * Shared setup for run, exec, and headless commands
 *
 * Extracts the duplicated env-var construction,
 * and sandbox-client creation shared by run.ts, exec.ts, and headless.ts.
 */

import type { SandboxClient } from '@spacecode/sandbox-client';
import type { ProxyMode } from '@spacecode/shared/remote';
import { SANDBOX_WORKER_URL } from '@spacecode/shared/remote';
import { resolveAuthToken, sandboxClientOptions } from '../config.js';
import { getGitHubToken } from '../sync/config.js';
import { buildEnvVars } from './env.js';

export interface RunSetupResult {
  client: SandboxClient;
  envVars: Record<string, string>;
}

/**
 * Gather config files, build env vars, and create a sandbox client.
 *
 * Shared by `run`, `exec`, and `headless` commands so the setup logic
 * lives in exactly one place.
 */
export async function setupRun(options?: { proxyMode?: ProxyMode }): Promise<RunSetupResult> {
  // Build env vars
  const ghToken = await getGitHubToken();
  const envVars = buildEnvVars({ ghToken, proxyMode: options?.proxyMode });

  // Create sandbox client
  const token = await resolveAuthToken();
  const { createSandboxClient } = await import('@spacecode/sandbox-client');
  const client = createSandboxClient(SANDBOX_WORKER_URL, token, sandboxClientOptions(token));

  return { client, envVars };
}

const GITHUB_TOKEN = /^(gh[pousr]_[A-Za-z0-9]{20,255}|github_pat_[A-Za-z0-9_]{20,255})$/;

type ExecParams = Parameters<SandboxClient['exec']>[0];

/** The core's answer for a task, or null when it is off (`SPACECODE_CORE_EXEC=off`) or not enabled for this user (403/404). */
export async function coreExecOrNull(params: ExecParams, signal?: AbortSignal) {
  if (process.env.SPACECODE_CORE_EXEC?.trim().toLowerCase() === 'off') return null;
  const { DEFAULT_API_URL } = await import('@spacecode/shared/remote');
  const { loadConfig } = await import('../config.js');
  const { execViaCore } = await import('./core-exec.js');
  const config = await loadConfig();
  const gh = params.envVars?.GH_TOKEN;
  return execViaCore({
    baseUrl: process.env.SPACECODE_API_URL ?? config.apiUrl ?? DEFAULT_API_URL,
    token: await resolveAuthToken(),
    repo: params.repo,
    prompt: params.prompt,
    ...(params.branch ? { branch: params.branch } : {}),
    ...(gh && GITHUB_TOKEN.test(gh) ? { ghToken: gh } : {}),
    ...(signal ? { signal } : {}),
  });
}

/**
 * `exec` through the core when the user's account has it (`POST /api/core/exec`, spec 2026-10-21-core-headless-routing-design.md),
 * else through the sandbox single loop as before. A 403/404 from the route means "not enabled"; every other failure throws.
 */
export async function execPreferringCore(
  client: SandboxClient,
  params: ExecParams,
  signal?: AbortSignal,
): Promise<Awaited<ReturnType<SandboxClient['exec']>>> {
  const viaCore = await coreExecOrNull(params, signal);
  if (viaCore) return { success: viaCore.success, logs: viaCore.logs, diff: viaCore.diff, exitCode: viaCore.exitCode };
  return client.exec(params, signal);
}
